import React, { useState, useEffect, useCallback, useMemo } from "react";
import { History, RefreshCw, Loader2 } from "lucide-react";
import {
  AppButton, StatusBadge, SelectControl, FilterToolbar, AppDataTable,
} from "../ui";
import apiClient from "../../utils/apiClient";

/**
 * AIAuditLogCard — every admin action taken on the WhatsApp AI.
 *
 * ⚠️ This table is SERVER-paginated (`skip`/`limit`, and the server reports the
 * total), so it gets the table's chrome and the server's own controls, and
 * **no column filters and no sort**. A funnel here would narrow the 25 rows in
 * the browser and say nothing about the other N — the same lie
 * `docs/design-system/TABLE_MIGRATION.md` opens with. The Action select is a
 * server parameter and belongs in the toolbar; sorting would need a `sort`
 * parameter the endpoint does not take.
 *
 * The card is `AppDataTable` itself — it has the same radius, border and
 * elevation as `AppCard`, so the old `AppCard` wrapper would have drawn a card
 * inside a card. The title lives in the table's `toolbar`.
 */

const PAGE_SIZE = 25;

const DASH = <span className="tw-text-content-subtle">—</span>;

const ACTIONS = [
  { value: "", label: "All actions" },
  { value: "config.update", label: "Config updated" },
  { value: "conversation.pause", label: "Conversation paused" },
  { value: "conversation.resume", label: "Conversation resumed" },
  { value: "conversation.override.set", label: "Override changed" },
  { value: "draft.send", label: "Draft sent" },
  { value: "draft.edit", label: "Draft edited & sent" },
  { value: "draft.discard", label: "Draft discarded" },
  { value: "knowledge.create", label: "KB chunk created" },
  { value: "knowledge.update", label: "KB chunk updated" },
  { value: "knowledge.delete", label: "KB chunk deleted" },
  { value: "knowledge.bulk_update", label: "KB bulk update" },
  { value: "knowledge.seed", label: "KB seeded" },
  { value: "knowledge.reindex", label: "KB re-embedded" },
];

/** An action → the tone its badge takes. These are the vocabulary's own words,
 *  so `StatusBadge` resolves them without a domain. */
const ACTION_TONE = {
  "config.update": "info",
  "conversation.pause": "warning",
  "conversation.resume": "success",
  "conversation.override.set": "info",
  "draft.send": "success",
  "draft.edit": "success",
  "draft.discard": "neutral",
  "knowledge.create": "success",
  "knowledge.update": "info",
  "knowledge.delete": "danger",
  "knowledge.bulk_update": "info",
  "knowledge.seed": "info",
  "knowledge.reindex": "neutral",
};

const AIAuditLogCard = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState("");
  // 1-based, because that is what `Paginator` speaks. The endpoint wants a row
  // offset, which is derived at the call rather than stored — two
  // representations of "which page" is how they drift apart.
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (action) params.set("action", action);
      params.set("limit", String(PAGE_SIZE));
      params.set("skip", String((page - 1) * PAGE_SIZE));
      const { data } = await apiClient.get(`/ai/whatsapp/audit?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [action, page]);

  useEffect(() => { load(); }, [load]);

  const columns = useMemo(() => [
    {
      key: "createdAt",
      label: "When",
      width: 170,
      // ⚠️ IST explicitly. The box is UTC and a bare toLocaleString names the
      // previous day for anything logged after 05:30 — on an audit log, that is
      // the column's entire job.
      render: (it) => (it.createdAt ? (
        <span className="tw-whitespace-nowrap tw-text-content-subtle">
          {new Date(it.createdAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
        </span>
      ) : DASH),
    },
    {
      key: "action",
      label: "Action",
      render: (it) => (
        <StatusBadge status={ACTION_TONE[it.action] || "neutral"}>{it.action}</StatusBadge>
      ),
    },
    {
      key: "actor",
      label: "Actor",
      render: (it) => it.actorName || it.actorRole || DASH,
    },
    { key: "summary", label: "Summary", render: (it) => it.summary || DASH },
  ], []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <AppDataTable
      fill={false}
      className="tw-mb-5"
      toolbar={
        <FilterToolbar
          flush
          filters={
            <SelectControl
              label="Action"
              value={action}
              // A new filter invalidates the page number — page 4 of "all
              // actions" is past the end of "KB deleted".
              onChange={(next) => { setPage(1); setAction(next); }}
              options={ACTIONS}
            />
          }
          actions={
            <AppButton size="sm" variant="outline" onClick={load} disabled={loading} aria-label="Refresh audit log">
              {loading
                ? <Loader2 size={12} className="tw-animate-spin" />
                : <RefreshCw size={12} />}
            </AppButton>
          }
        >
          <span className="tw-flex tw-items-center tw-gap-2 tw-text-[0.8125rem] tw-font-semibold tw-text-content">
            <History size={16} /> AI action audit log
          </span>
        </FilterToolbar>
      }
      columns={columns}
      rows={items}
      rowKey={(it) => it._id}
      loading={loading}
      emptyMessage="No audit entries. Admin actions on AI config, drafts, conversations and the knowledge base appear here."
      pagination={totalPages > 1 ? {
        page,
        totalPages,
        total,
        pageSize: PAGE_SIZE,
        onChange: setPage,
        infoLabel: "entries",
      } : undefined}
    />
  );
};

export default AIAuditLogCard;
